import React from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { 
  AccountCircle, 
  Menu as MenuIcon,
  Home,
  School,
  People,
  Settings,
  Lock,
  Logout,
  Apps as AppsIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import AppSwitcher from './AppSwitcher';
import Logo from '../phoenix.png';

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const { canManageUsers, canManagePrerequisites } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:720px)');
  
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [appSwitcherOpen, setAppSwitcherOpen] = React.useState(false);

  // Debug logging
  React.useEffect(() => {
    console.log('Navbar mounted, user:', user?.first_name, 'isMobile:', isMobile, 'appSwitcherOpen:', appSwitcherOpen);
  }, [user, isMobile, appSwitcherOpen]);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleAppSwitcherToggle = () => {
    console.log('handleAppSwitcherToggle called, current state:', appSwitcherOpen);
    setAppSwitcherOpen(!appSwitcherOpen);
    console.log('New state set to:', !appSwitcherOpen);
  };

  const handleAppSwitcherClose = () => {
    setAppSwitcherOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    handleClose();
    handleMobileMenuClose();
    navigate('/login');
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleMobileMenuClose();
    // Also close app switcher when navigating
    handleAppSwitcherClose();
  };

  const getCurrentAppName = () => {
    switch (location.pathname) {
      case '/':
        return 'Home';
      case '/award-check':
        return 'Prerequisites';
      case '/attendance':
        return 'Attendance';
      // Deprecated modules:
      // case '/course-scraper':
      //   return 'Course Scraper';
      // case '/course-upload':
      //   return 'Course Builder';
      case '/temp-cards':
        return 'Temp Cards';
      case '/test-sheet-submissions':
        return 'Test Sheet Submissions';
      case '/dob-verifier':
        return 'DOB Verifier';
      case '/company-policies':
        return 'Company Policies';
      case '/lss-invoices':
        return 'LSS Invoices';
      // case '/object-purge':
      //   return 'Object Purge';
      default:
        return 'LSS Tools';
    }
  };

  return (
    <Box sx={{ flexGrow: 1, position: 'relative', overflow: 'visible' }}>
      <AppBar 
        position="static" 
        sx={{ 
          position: 'relative', 
          overflow: 'visible',
          backgroundColor: '#27445C', // Dark navy color
          '&:hover': {
            backgroundColor: '#27445C', // Slightly darker navy on hover
          }
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="logo"
            onClick={() => handleNavigation('/')}
            sx={{ mr: 2 }}
          >
            <img src={Logo} alt="Logo" style={{ height: 60 }} />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            LSS Tools
            {!isMobile && (
              <Typography 
                variant="body2" 
                component="span" 
                sx={{ 
                  ml: 2, 
                  opacity: 0.8, 
                  fontWeight: 400,
                  fontSize: '0.9rem'
                }}
              >
                › {getCurrentAppName()}
              </Typography>
            )}
          </Typography>
          
          {user ? (
            <>
              {/* Desktop Navigation */}
              {!isMobile && (
                <>
                  <Button
                    color="inherit"
                    startIcon={<AppsIcon />}
                    endIcon={<ExpandMoreIcon sx={{ 
                      transform: appSwitcherOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease'
                    }} />}
                    onClick={handleAppSwitcherToggle}
                    data-app-switcher-button
                    sx={{ 
                      mr: 2,
                      textTransform: 'none',
                      fontWeight: 500,
                      backgroundColor: appSwitcherOpen ? 'rgba(255,255,255,0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.1)',
                      }
                    }}
                  >
                    Switch Apps
                  </Button>
                  
                  {/* Admin Navigation */}
                  {(canManageUsers() || canManagePrerequisites()) && (
                    <>
                      {canManageUsers() && (
                        <Button
                          color="inherit"
                          onClick={() => navigate('/users')}
                          sx={{ 
                            mr: 1,
                            backgroundColor: location.pathname === '/users' ? 'rgba(255,255,255,0.1)' : 'transparent',
                          }}
                        >
                          User Management
                        </Button>
                      )}
                      {canManagePrerequisites() && (
                        <Button
                          color="inherit"
                          onClick={() => navigate('/prerequisites')}
                          sx={{ 
                            mr: 1,
                            backgroundColor: location.pathname === '/prerequisites' ? 'rgba(255,255,255,0.1)' : 'transparent',
                          }}
                        >
                          Prerequisites
                        </Button>
                      )}
                    </>
                  )}
                  <IconButton
                    size="large"
                    aria-label="account of current user"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={handleMenu}
                    color="inherit"
                  >
                    <AccountCircle />
                  </IconButton>
                  <Menu
                    id="menu-appbar"
                    anchorEl={anchorEl}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                  >
                    <MenuItem disabled>
                      {user.first_name} {user.last_name}
                    </MenuItem>
                    <MenuItem onClick={() => { navigate('/account'); handleClose(); }}>
                      Change Password
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>Logout</MenuItem>
                  </Menu>
                </>
              )}

              {/* Mobile Hamburger Menu */}
              {isMobile && (
                <IconButton
                  size="large"
                  edge="end"
                  color="inherit"
                  aria-label="menu"
                  onClick={handleMobileMenuToggle}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </>
          ) : (
            <Button color="inherit" onClick={() => navigate('/login')}>
              Login
            </Button>
          )}
        </Toolbar>
        
        {/* App Switcher Dropdown */}
        {user && !isMobile && (
          <AppSwitcher 
            open={appSwitcherOpen} 
            onClose={handleAppSwitcherClose}
          />
        )}
      </AppBar>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 250,
          },
        }}
      >
        <Box
          sx={{ width: 250 }}
          role="presentation"
        >
          {user && (
            <>
              {/* User Info */}
              <Box sx={{ p: 2, backgroundColor: theme.palette.primary.main, color: 'white' }}>
                <Typography variant="h6" noWrap>
                  {user.first_name} {user.last_name}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {user.email}
                </Typography>
              </Box>
              
              <List>
                {/* Main Navigation Items */}
                <ListItem 
                  button 
                  onClick={() => handleNavigation('/')}
                  sx={{ 
                    backgroundColor: location.pathname === '/' ? 'rgba(0,0,0,0.04)' : 'transparent' 
                  }}
                >
                  <ListItemIcon>
                    <Home />
                  </ListItemIcon>
                  <ListItemText primary="Home" />
                </ListItem>
                
                <ListItem 
                  button 
                  onClick={() => handleNavigation('/award-check')}
                  sx={{ 
                    backgroundColor: location.pathname === '/award-check' ? 'rgba(0,0,0,0.04)' : 'transparent' 
                  }}
                >
                  <ListItemIcon>
                    <School />
                  </ListItemIcon>
                  <ListItemText primary="Prerequisites" />
                </ListItem>

                <ListItem 
                  button 
                  onClick={() => handleNavigation('/attendance')}
                  sx={{ 
                    backgroundColor: location.pathname === '/attendance' ? 'rgba(0,0,0,0.04)' : 'transparent' 
                  }}
                >
                  <ListItemIcon>
                    <People />
                  </ListItemIcon>
                  <ListItemText primary="Attendance" />
                </ListItem>

                {/* Deprecated: Verify Posted Courses / Course Scraper navigation
                <ListItem 
                  button 
                  onClick={() => handleNavigation('/course-scraper')}
                  sx={{ 
                    backgroundColor: location.pathname === '/course-scraper' ? 'rgba(0,0,0,0.04)' : 'transparent' 
                  }}
                >
                  <ListItemIcon>
                    <School />
                  </ListItemIcon>
                  <ListItemText primary="Course Scraper" />
                </ListItem>
                */}

                {/* Admin Navigation Items */}
                {user.role === 'admin' && (
                  <>
                    <Divider />
                    <ListItem 
                      button 
                      onClick={() => handleNavigation('/users')}
                      sx={{ 
                        backgroundColor: location.pathname === '/users' ? 'rgba(0,0,0,0.04)' : 'transparent' 
                      }}
                    >
                      <ListItemIcon>
                        <People />
                      </ListItemIcon>
                      <ListItemText primary="User Management" />
                    </ListItem>
                    
                    <ListItem 
                      button 
                      onClick={() => handleNavigation('/prerequisites')}
                      sx={{ 
                        backgroundColor: location.pathname === '/prerequisites' ? 'rgba(0,0,0,0.04)' : 'transparent' 
                      }}
                    >
                      <ListItemIcon>
                        <Settings />
                      </ListItemIcon>
                      <ListItemText primary="Prerequisites" />
                    </ListItem>
                  </>
                )}

                {/* Account Navigation Items */}
                <Divider />
                <ListItem 
                  button 
                  onClick={() => handleNavigation('/account')}
                  sx={{ 
                    backgroundColor: location.pathname === '/account' ? 'rgba(0,0,0,0.04)' : 'transparent' 
                  }}
                >
                  <ListItemIcon>
                    <Lock />
                  </ListItemIcon>
                  <ListItemText primary="Change Password" />
                </ListItem>
                
                <ListItem button onClick={handleLogout}>
                  <ListItemIcon>
                    <Logout />
                  </ListItemIcon>
                  <ListItemText primary="Logout" />
                </ListItem>
              </List>
            </>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default Navbar;
